import type { Course, CourseModule } from '@/components/education/types';

// Pure edits to a course's unit list. Each returns a new course with
// totalLessons kept in step, so the caller can save it and also keep it as
// the source for the next edit (namat#75: the next add used a stale list).

const withModules = (course: Course, modules: CourseModule[]): Course => ({
  ...course,
  modules,
  totalLessons: modules.length,
});

export const appendModule = (course: Course, module: CourseModule): Course =>
  withModules(course, [...(course.modules || []), module]);

export const removeModuleAt = (course: Course, index: number): Course =>
  withModules(course, (course.modules || []).filter((_, i) => i !== index));

export function moveModule(course: Course, index: number, step: -1 | 1): Course {
  const modules = [...(course.modules || [])];
  const target = index + step;
  if (target < 0 || target >= modules.length) return course;
  [modules[index], modules[target]] = [modules[target], modules[index]];
  return withModules(course, modules);
}

export function duplicateModuleAt(course: Course, index: number, newId: string): Course {
  const modules = [...(course.modules || [])];
  const original = modules[index];
  if (!original) return course;
  modules.splice(index + 1, 0, { ...original, id: newId, title: `${original.title} (Copy)`, completed: false });
  return withModules(course, modules);
}

export const replaceModule = (course: Course, module: CourseModule): Course =>
  withModules(
    course,
    (course.modules || []).map((m) => (m.id === module.id ? { ...m, ...module } : m)),
  );
